
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";

export default function UpdatePassword() {
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  // Check if we're in a password reset flow
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      // If user has no session and no hash in URL, redirect to login
      if (!data.session && !window.location.hash) {
        toast.error("Link de redefinição inválido ou expirado");
        navigate("/login");
      }
    };

    checkSession();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    
    if (!password || !passwordConfirm) {
      setErrorMsg("Por favor, preencha todos os campos");
      toast.error("Por favor, preencha todos os campos");
      return;
    }
    
    if (password !== passwordConfirm) {
      setErrorMsg("As senhas não coincidem");
      toast.error("As senhas não coincidem");
      return;
    }
    
    if (password.length < 6) {
      setErrorMsg("A senha deve ter pelo menos 6 caracteres");
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    
    try {
      setIsLoading(true);
      
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) throw error;
      
      toast.success("Senha atualizada com sucesso!");
      // Short delay to show success message before redirecting
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (error: any) {
      console.error("Erro ao atualizar senha:", error);
      let message = "Erro ao atualizar senha";
      
      if (error.message) {
        message = error.message;
      }
      
      setErrorMsg(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-primary">Minha Agência</h1>
        <p className="text-gray-600 mt-2">
          Crie sua nova senha
        </p>
      </div>
      
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Definir nova senha</CardTitle>
          <CardDescription>
            Por favor, crie uma nova senha para sua conta
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {errorMsg && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start text-sm text-red-800 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
                <AlertCircle className="h-4 w-4 mr-2 mt-0.5 text-red-600 dark:text-red-400" />
                <span>{errorMsg}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="password">Nova Senha</Label>
              <Input 
                id="password" 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <p className="text-xs text-muted-foreground">A senha deve ter pelo menos 6 caracteres</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="passwordConfirm">Confirmar Nova Senha</Label>
              <Input 
                id="passwordConfirm" 
                type="password" 
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                minLength={6}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={isLoading}
            >
              {isLoading ? "Atualizando..." : "Atualizar senha"}
            </Button>
            <div className="text-center text-sm">
              Lembrou a senha?{" "}
              <Link 
                to="/login" 
                className="text-primary hover:underline"
              >
                Voltar ao login
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
